<template>
  <fragment>
    <btn
      :disabled="(!loadingCurrentToken && !currentUrl) || buttonClicked"
      :loading-spinner-variant="!hasThumbnailImageSlot ? 'white-static' : ''"
      v-bind="{ ...$attrs }"
      :variant="hasThumbnailImageSlot ? 'thumbnail-link' : variant"
      :loading="isLoading"
      class="dicom-viewer-button"
      :class="{ 'med-dream-link': !hasThumbnailImageSlot }"
      :size="!hasThumbnailImageSlot ? 'sm' : ''"
      :tag="linkTag"
      @click.prevent.stop="onButtonClick"
    >
      <fragment v-if="hasThumbnailImageSlot">
        <slot name="thumbnail-link"></slot>
      </fragment>
      <fragment v-else>
        <span class="fa-duotone fa-external-link-alt fa-fw"></span>
        <span class="d-none d-sm-inline">{{ buttonText }}</span>
        <span class="d-sm-none">Review Mode</span>
      </fragment>
    </btn>
  </fragment>
</template>

<script>
  import flags from '@/config/feature-flags';
  import { mapActions, mapState, mapGetters } from 'vuex';
  import { CaseResource } from '@/api-resources';
  import { paramsAreDifferent } from '@/helpers';
  import { isResearch } from '@/utils/routing/routing';

  export default {
    name: 'DicomViewerButton',

    props: {
      segmentationUids: { type: Array, default: () => [] },
      segmentationOptions: [Object, null, undefined],
      variant: { type: String, default: 'primary' },
      keepOpen: { type: Boolean, default: true },
      autoloadSegmentations: Boolean,
      targetCase: CaseResource,
      useSegmentation: Boolean,
      viewerReadOnly: Boolean,
      collectionCase: null,
      autoOpen: Boolean,
      studies: Array,
      ignorePatientRestrictions: {
        type: Boolean,
        default: false,
      },
      buttonText: {
        type: String,
        default() {
          return this.$t('cmrad.open_review_mode');
        },
      },
    },

    watch: {
      studies: {
        deep: true,
        immediate: false,
        handler(newStudies, oldStudies) {
          if (paramsAreDifferent(newStudies, oldStudies)) {
            this.reinitializeButton();
          }
        },
      },

      viewerOptions: {
        deep: true,
        immediate: false,
        handler(newOptions, oldOptions) {
          if (paramsAreDifferent(newOptions, oldOptions)) {
            this.reinitializeButton();
          }
        },
      },

      currentUrl(newVal) {
        this.urlChanged = true;
        if (newVal && this.externalTriggerClick) {
          this.externalClick();
        }
      },
    },
    data() {
      return {
        urlChanged: false,
        buttonClicked: false,
      };
    },

    created() {
      this.initializeState();
    },

    mounted() {
      this.$nextTick(() => {
        this.initializeViewer();
      });
    },

    beforeDestroy() {
      this.onViewerDestroy();
    },

    computed: {
      ...mapGetters(['isFeatureEnabled']),
      ...mapState('dicomViewer', {
        isViewerWindowOpen: 'viewerOpen',
        externalTriggerClick: 'buttonExternallyTriggered',
      }),

      ...mapGetters('dicomViewer', {
        isTokenLoadedForStudies: 'isTokenLoadedForStudies',
        getViewerToken: 'getTokenForStudies',
        getViewerUrl: 'getUrlForStudies',
      }),

      viewerOptions() {
        let onSegmentation = () => {};

        if (!this.viewerReadOnly && this.useSegmentation) {
          onSegmentation = (segmentationData) => this.$emit('segmentation', segmentationData);
        }

        return {
          readOnly: this.viewerReadOnly,
          segmentation: this.useSegmentation,
          segmentationUids: this.segmentationUids,
          segmentationOptions: this.segmentationOptions,
          autoloadSegmentations: this.autoloadSegmentations,
          keepOpen: this.keepOpen,
          onSegmentation,
          skipRestrictions: this.ignorePatientRestrictions,
          isSegmentationSaveStartedEventSubscriptionEnabled: this.isSegmentationSaveStartedEventSubscriptionEnabled,
        };
      },

      viewerParams() {
        if (!this.targetCase && !this.collectionCase) {
          return {
            studies: this.studies,
            keyImages: [],
            options: this.getViewerOptions(),
            appSection: this.$page?.props?.appSection,
          };
        }

        return {
          studies: this.studies,
          keyImages: this.targetCase ? this.targetCase.keyImages : this.collectionCase.keyImages,
          options: this.getViewerOptions(),
          appSection: this.$page?.props?.appSection,
        };
      },

      registerParams() {
        // The API expects an incremental id, but some targetCase are being passed with id as string even if prop typing (CaseResource) says it's a number
        let caseId = this.targetCase?.id ?? this.collectionCase?.id;
        if (typeof this.targetCase?.id === 'string' && typeof this.targetCase?.caseId === 'number') {
          caseId = this.targetCase?.caseId;
        }

        const { project } = this.$route().params;

        return {
          studies: this.studies,
          options: this.getViewerOptions(),
          caseId,
          projectUuid: project,
        };
      },

      loadingCurrentToken() {
        return this.isTokenLoadedForStudies(this.studies, this.getViewerOptions());
      },

      currentUrl() {
        return this.getViewerUrl(this.studies, this.getViewerOptions());
      },

      currentToken() {
        return this.getViewerToken(this.studies, this.getViewerOptions());
      },

      shouldAutoOpen() {
        return this.autoOpen && this.isViewerWindowOpen;
      },

      linkTag() {
        return isResearch() ? 'button' : 'a';
      },

      isSegmentationSaveStartedEventSubscriptionEnabled() {
        return this.isFeatureEnabled(flags.RESEARCH_MEDDREAM_SEGMENTATION_SAVE_STARTED_EVENT_SUBSCRIPTION);
      },

      hasThumbnailImageSlot() {
        return !!this.$slots['thumbnail-link'];
      },

      isLoading() {
        return !this.hasThumbnailImageSlot ? this.loadingCurrentToken : false;
      },
    },

    methods: {
      ...mapActions('dicomViewer', {
        unregisterButton: 'unregisterButton',
        registerButton: 'registerButton',
        initializeState: 'initialize',
        closeViewer: 'closeViewer',
        isViewerOpen: 'isViewerOpen',
        openViewer: 'openViewer',
        updateButtonExternallyTriggered: 'updateButtonExternallyTriggered',
        updateSegmentationCallback: 'updateSegmentationCallback',
      }),
      ...mapActions('repositoryAuditLog', ['triggerAudit']),
      ...mapActions(['importImages']),

      onButtonClick() {
        if (isResearch()) {
          this.buttonClicked = true;
        }
        const { event, project, repository, subject } = this.$route().params;
        const [{ studyId }] = this.viewerParams.studies;
        const logResearhAuditLog = () =>
          isResearch()
            ? this.triggerAudit({
                auditLogName: this.viewerReadOnly ? 'eventOpenedDicomDataRo' : 'eventOpenedDicomDataRw',
                repository,
                project,
                studyId,
                subject,
                event,
              })
            : Promise.resolve();
        Promise.all([logResearhAuditLog(), this.openViewer(this.viewerParams)])
          .catch(() => {})
          .finally(() => {
            this.buttonClicked = false;
            this.updateButtonExternallyTriggered(false);
          });
      },

      getViewerOptions() {
        return { ...this.viewerOptions };
      },

      onWindowFocus() {
        if (this.isViewerWindowOpen) {
          this.notifyUpdate();
        }
      },

      notifyUpdate() {
        if (!this.viewerReadOnly && this.targetCase) {
          this.importImages(this.targetCase).catch(() => {});
        }

        this.$emit('studies-updated-from-viewer', this.viewerParams.studies);
      },

      onViewerDestroy() {
        if (this.isViewerWindowOpen) {
          if (!this.keepOpen) {
            this.closeViewer(this.studies, { ...this.getViewerOptions() });
          }

          this.unregisterButton({
            studies: this.studies,
            options: { ...this.getViewerOptions() },
          });
        }

        if (window) {
          window.removeEventListener('focus', this.onWindowFocus);
        }

        this.urlChanged = false;
      },

      async initializeViewer() {
        this.registerButton(this.registerParams);

        // Use the action (not the cached Vuex flag) so we reconcile state.viewerOpen
        // with the real popup state. If the user closed the popup manually, the
        // Vuex flag may still be true; isViewerOpen will correct it and prevent us
        // from posting to a closed window (which would spawn a blank tab via the
        // meddream lib's window.open fallback).
        const open = await this.isViewerOpen(this.studies, { ...this.getViewerOptions() });

        if (open && this.useSegmentation) {
          this.updateSegmentationCallback({
            studies: this.studies,
            options: { ...this.getViewerOptions() },
          });
        }

        if (this.shouldAutoOpen) {
          this.doAutoOpen();
        }

        if (window) {
          window.addEventListener('focus', this.onWindowFocus);
        }
      },

      async doAutoOpen() {
        this.openViewer(this.viewerParams);
      },

      reinitializeButton() {
        if (this.isViewerWindowOpen && !this.useSegmentation) {
          this.onViewerDestroy();
        }

        this.$nextTick(() => {
          this.initializeViewer();
        });
      },

      async externalClick() {
        const open = await this.isViewerOpen(this.studies, { ...this.getViewerOptions() });

        if (open) {
          this.onButtonClick();
        }
      },
    },
  };
</script>
